import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  User,
  Calendar,
  Mail,
  Trophy,
  Bookmark,
  BookOpen,
  Link as LinkIcon,
  Eye,
  FileText
} from "lucide-react";
import { Pencil, Image as ImageIcon } from "lucide-react";
import { MenuItem, TextField, Grid, Button } from '@mui/material';
import { useAuth } from '../../services/AuthContext.js'; 
import { AccountSpecificMessage} from '../../components/EnrollButton.js';
import "./account_edit.css";

const baseAPI_URL = process.env.REACT_APP_BACKEND_API_BASE_ENDPOINT;

const AccountEdit = () => {
  const { user, loading, getIdToken } = useAuth(); 
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [biography, setBiography] = useState('');
  const [link, setLink] = useState('');
  const [profilePhoto, setProfilePhoto] = useState();
  const [errors, setErrors] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const default_profile_image_url = "https://dominio-eletrico-profile-images-dev.s3.us-east-1.amazonaws.com/default_user_image.jpg";
  function normalizeUrl(url) {
    if (!url) return "";

    let trimmed = url.trim();

    // If it already starts with a protocol, keep it
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }

    // Otherwise, assume https
    return `https://${trimmed}`;
  }

  function isValidUrl(url) {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  async function loadAccountInfo() {
    try{ 
      const token = await getIdToken();
      const response = await fetch(`${baseAPI_URL}/users/get-account-info`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setName(data.full_name ?? '');
        setSlug(data.slug ?? '');
        setBiography(data.biography ?? '');
        setLink(data.link ?? '');
        setProfilePhoto(
          data.profile_photo ? `${data.profile_photo}?t=${Date.now()}` : null
        );
      }
    } catch (err) {
      setErrors("Não foi possível carregar as informações atuais da sua conta.");
    }
  }

  async function handleSubmit() {
    const normalizedLink = normalizeUrl(link);

    if (!isValidUrl(normalizedLink)) {
      setErrors("Digite um link válido.");
      return;
    }
    const payload = {
      full_name: name,
      slug: slug,
      link: normalizedLink,
      biography: biography
    }
    try {
      const token = await getIdToken();
      const response = await fetch(`${baseAPI_URL}/users/update-user-public-info`, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`
        }, 
        body: JSON.stringify(payload)
      })
      const result = await response.json();
      if (result.status === "success") {
        setResponseMessage("Informações salvas com sucesso.");
        setErrors("");
      } else {
        setResponseMessage("");
        setErrors(result.message);
      }
      return result;
    } catch (error) {
      console.error("Error creating charge:", error);
    }
  }

  const handlePhotoClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErrors("Selecione uma imagem válida.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setErrors("A imagem deve ter no máximo 2MB.");
      return;
    }

    try {
      setUploading(true);
      setErrors("");

      const token = await getIdToken();

      const presignRes = await fetch(`${baseAPI_URL}/users/gen-presigned-url-profile-image`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          fileType: file.type
        })
      });

      if (!presignRes.ok) {
        throw new Error("Erro ao gerar URL de upload");
      }

      const { uploadUrl, fileUrl } = await presignRes.json();
      setProfilePhoto(fileUrl);

      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type
        },
        body: file
      });

      if (!uploadRes.ok) {
        throw new Error("Erro ao enviar imagem");
      }

      const saveRes = await fetch(`${baseAPI_URL}/users/update-profile-photo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          profile_image: fileUrl
        })
      });

      if (!saveRes.ok) {
        throw new Error("Erro ao salvar imagem");
      }

      setResponseMessage("Foto de perfil atualizada com sucesso.");
      setErrors("");
      await loadAccountInfo();

    } catch (err) {
      console.error(err);
      setResponseMessage("");
      setErrors("Erro ao atualizar foto.");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    loadAccountInfo();
  }, [user]);

  useEffect(() => {
    setImageError(false);
  }, [profilePhoto]);
  const MAX_BIO_LENGTH = 500;

  return (
    <div className="account-edit-page">
      <div className="account-edit-card">
        <h1 className="account-edit-title">Edição de dados pessoais</h1>

        <p className="account-edit-subtitle">
          Edite os dados que aparecem na sua página pública do site.
        </p>

        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        <div className="account-edit-photo-block">
          <div className="account-edit-photo" onClick={handlePhotoClick}>
            <div className="photo-wrapper">
              <img
                src={profilePhoto && !imageError ? profilePhoto : default_profile_image_url}
                alt="Foto de perfil"
                className="account-photo-img-edit"
                onError={() => setImageError(true)}
              />

              <div className="photo-overlay">
                <Pencil size={18} />
                <span>{uploading ? "Enviando..." : "Alterar foto"}</span>
              </div>
            </div>
          </div>

          <button type="button" className="change-photo-link" onClick={handlePhotoClick} disabled={uploading}>
            <Pencil size={16} />
            <span>{uploading ? "Enviando..." : "Alterar foto"}</span>
          </button>

          <a className="account-edit-public-link" href={`/user/${slug}`}>
            <Eye size={16} /> Ver página pública
          </a>
        </div>

        <div className="account-edit-fields">
          <TextField
            label="Nome"
            variant="outlined"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          <TextField
            label="Slug (o @ da página pública)"
            variant="outlined"
            fullWidth
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
          />

          <TextField
            label="Link"
            variant="outlined"
            fullWidth
            value={link}
            onChange={(e) => setLink(e.target.value)}
            onBlur={() => {
              if (!link) return;

              const normalized = normalizeUrl(link);

              if (normalized !== link) {
                setLink(normalized);
              }

              if (!isValidUrl(normalized)) {
                setErrors("Digite um link válido.");
              } else {
                setErrors("");
              }
            }}
            required
          />

          <TextField
            label="Bio"
            variant="outlined"
            fullWidth
            required
            multiline
            rows={4}
            value={biography}
            onChange={(e) => setBiography(e.target.value)}
            inputProps={{
                maxLength: MAX_BIO_LENGTH
            }}
            helperText={`${biography.length}/${MAX_BIO_LENGTH}`}
            FormHelperTextProps={{
                style: { textAlign: 'right' }
            }}
          />
        </div>

        {errors && <p className="account-edit-error">{errors}</p>}

        {responseMessage && <p className="account-edit-success">{responseMessage}</p>}

        <div className="account-edit-footer">
          <Button
            onClick={handleSubmit}
            className="account-edit-save-btn"
            variant="contained"
          >
            Salvar
          </Button>
        </div>

        <p className="account-edit-trust">🔒 Dados seguros e protegidos.</p>
      </div>
    </div>
  );
};

export default AccountEdit;
